import { OPEN_STATUSES } from "@procuremate/shared";
import { CircleAlert, CircleCheck, Send } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/features/auth/api";
import type { Language } from "@/i18n";
import { ApiRequestError } from "@/lib/api-client";
import { useFieldCatalogue, useRequestDetail, useSubmitRequest } from "./api";
import { DetailsBySection, FieldRow, FindingList, requesterFindings } from "./request-panel";

/**
 * Review & submit (ADR-0014, WP-2.4): what goes to procurement, what will be recorded as not
 * provided ("submit anyway"), and the confirmation once submitted. The server's gate decides;
 * this page only reflects it.
 */
export function SubmitPage() {
  const { id = "" } = useParams();
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const me = useCurrentUser();
  const detail = useRequestDetail(id);
  const catalogue = useFieldCatalogue();
  const submit = useSubmitRequest(id);
  const heading = useRef<HTMLHeadingElement>(null);
  const submitted = detail.data ? !OPEN_STATUSES.has(detail.data.status) : false;

  // The confirmation replaces the review in place: move focus to its heading.
  useEffect(() => {
    if (submitted) heading.current?.focus();
  }, [submitted]);

  if (detail.isPending) return <p className="text-fg-muted">{t("app.loading")}</p>;
  if (detail.isError) {
    return (
      <section className="rounded-lg bg-surface p-6 shadow-card">
        <p role="alert">{t("requests.page.notFound")}</p>
      </section>
    );
  }

  const request = detail.data;
  const labelOf = (key: string) => catalogue.data?.fields.find((f) => f.key === key)?.labels[language] ?? key;
  const backLink = (
    <Link
      to={`/requests/${id}`}
      className="inline-flex h-10 items-center justify-center rounded-md border border-border bg-surface px-4 text-sm font-semibold text-primary hover:bg-bg"
    >
      {t("requests.submit.back")}
    </Link>
  );

  if (submitted) {
    return (
      <section className="overflow-hidden rounded-lg bg-surface shadow-card">
        {/* Brand moment (BRAND.md): the gradient is decorative, the text stays on white. */}
        <div aria-hidden="true" className="h-2 bg-gradient-brand" />
        <div className="flex flex-col items-start gap-3 p-6 sm:p-8">
          <CircleCheck aria-hidden="true" className="h-10 w-10 text-success" />
          <h1 ref={heading} tabIndex={-1} className="text-2xl font-bold text-primary outline-none">
            {t("requests.submit.doneTitle")}
          </h1>
          <p className="max-w-prose text-fg">{t("requests.submit.doneBody", { reference: request.reference })}</p>
          <div className="mt-2 flex flex-wrap gap-3">
            <Link
              to={`/requests/${id}`}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-on-primary hover:bg-primary-hover"
            >
              {t("requests.submit.viewRequest")}
            </Link>
            <Link
              to="/"
              className="inline-flex h-10 items-center justify-center rounded-md border border-border bg-surface px-4 text-sm font-semibold text-primary hover:bg-bg"
            >
              {t("requests.submit.allRequests")}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const { gate } = request;
  const isRequester = me.data?.id === request.requesterId;
  const classified = request.engagementShape !== null && request.sizeTier !== null;
  const mustAnswer = gate.openMandatory.filter((k) => !gate.skippable.includes(k));
  const recorded = request.fields.filter((f) => f.status === "CAPTURED" || f.status === "NOT_APPLICABLE");
  const refused = submit.error instanceof ApiRequestError && submit.error.status === 409;

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-lg bg-surface p-4 shadow-card sm:p-6">
        <h1 className="text-2xl font-bold text-primary">
          {t("requests.submit.title")} <span className="tabular text-fg">{request.reference}</span>
        </h1>
        {request.title ? <p className="mt-1 font-semibold text-fg">{request.title}</p> : null}
        <p className="mt-2 max-w-prose text-fg-muted">{t("requests.submit.intro")}</p>

        {!gate.canSubmit && gate.canSubmitAnyway ? (
          <div className="mt-4 rounded-md bg-warning-bg p-4 text-warning-fg">
            <h2 className="flex items-center gap-1.5 font-heading text-base font-semibold">
              <CircleAlert aria-hidden="true" className="h-4 w-4" />
              {t("requests.submit.missingTitle")}
            </h2>
            <p className="mt-1 text-sm">{t("requests.submit.missingBody")}</p>
            <ul className="mt-2 list-disc pl-5 text-sm">
              {gate.skippable.map((k) => (
                <li key={k}>{labelOf(k)}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {!gate.canSubmitAnyway ? (
          <div className="mt-4 rounded-md border border-danger bg-danger-bg p-4 text-fg">
            <h2 className="flex items-center gap-1.5 font-heading text-base font-semibold text-danger">
              <CircleAlert aria-hidden="true" className="h-4 w-4" />
              {t("requests.submit.blockedTitle")}
            </h2>
            {classified ? (
              <>
                <p className="mt-1 text-sm">{t("requests.submit.blockedBody")}</p>
                <ul className="mt-2 list-disc pl-5 text-sm">
                  {mustAnswer.map((k) => (
                    <li key={k}>{labelOf(k)}</li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="mt-1 text-sm">{t("requests.submit.unclassifiedBody")}</p>
            )}
          </div>
        ) : null}

        {refused ? (
          <p role="alert" className="mt-4 text-sm text-danger">
            {t("requests.submit.refused")}
          </p>
        ) : submit.isError ? (
          <p role="alert" className="mt-4 text-sm text-danger">
            {t("requests.submit.error")}
          </p>
        ) : null}
        {!isRequester ? <p className="mt-4 text-sm text-fg-muted">{t("requests.submit.notRequester")}</p> : null}

        <div className="mt-5 flex flex-wrap gap-3">
          {isRequester && gate.canSubmitAnyway ? (
            <Button
              aria-disabled={submit.isPending || undefined}
              onClick={() => {
                if (!submit.isPending) submit.mutate(!gate.canSubmit);
              }}
            >
              <Send aria-hidden="true" className="h-4 w-4" />
              {submit.isPending
                ? t("requests.submit.submitting")
                : gate.canSubmit
                  ? t("requests.submit.submit")
                  : t("requests.submit.submitAnyway")}
            </Button>
          ) : null}
          {backLink}
        </div>
      </section>

      {requesterFindings(request.findings).length > 0 ? (
        <section aria-labelledby="submit-findings" className="rounded-lg bg-surface p-4 shadow-card sm:p-6">
          <h2 id="submit-findings" className="mb-2 font-heading text-lg font-semibold text-fg">
            {t("requests.submit.findings")}
          </h2>
          <FindingList findings={request.findings} catalogue={catalogue.data} />
        </section>
      ) : null}

      <section aria-labelledby="submit-details" className="rounded-lg bg-surface p-4 shadow-card sm:p-6">
        <h2 id="submit-details" className="mb-2 font-heading text-lg font-semibold text-fg">
          {t("requests.submit.details")}
        </h2>
        {recorded.length === 0 ? (
          <p className="text-sm text-fg-muted">{t("requests.panel.noDetails")}</p>
        ) : (
          <DetailsBySection
            fields={recorded}
            catalogue={catalogue.data}
            row={(key, field) => (
              <FieldRow
                key={key}
                requestId={id}
                fieldKey={key}
                entry={catalogue.data?.fields.find((f) => f.key === key)}
                field={field}
                canEdit={false}
                editBlocked
              />
            )}
          />
        )}
      </section>
    </div>
  );
}
